/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { useState, useEffect, useCallback } from 'preact/compat';
import { events } from '@adobe-commerce/event-bus';
import { updateQuoteTemplateItemQuantities } from '@/quote-management/api/updateQuoteTemplateItemQuantities';
import { CartItemModel } from '@/quote-management/data/models/negotiable-quote-model';

const NOTIFICATION_AUTO_DISMISS_DELAY = 3000;

export interface UseUpdateTemplateQuantitiesReturn {
  quantityChanges: Record<string, number>;
  handleQuantityChange: (item: CartItemModel, newQuantity: number) => void;
  handleUpdate: (e: SubmitEvent) => void;
  handleConfirmUpdate: () => Promise<void>;
  handleCancelUpdate: () => void;
  handleDismissBanner: () => void;
  isUpdateModalOpen: boolean;
  isUpdating: boolean;
  updateNotificationState: {
    type: 'success' | 'error' | null;
    message: string;
  };
}

export interface UseUpdateTemplateQuantitiesParams {
  templateId?: string;
  onUpdateModalStateChange?: (isOpen: boolean) => void;
  updateSuccessMessage: string;
  updateErrorMessage: string;
}

/**
 * Custom hook to manage updating quantities of items in a quote template
 */
export const useUpdateTemplateQuantities = (
  params: UseUpdateTemplateQuantitiesParams
): UseUpdateTemplateQuantitiesReturn => {
  const {
    templateId,
    onUpdateModalStateChange,
    updateSuccessMessage,
    updateErrorMessage,
  } = params;

  const [quantityChanges, setQuantityChanges] = useState<Record<string, number>>({});
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateNotificationState, setUpdateNotificationState] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  // Listen for quote template data event (successful update)
  useEffect(() => {
    const templateDataEvent = events.on(
      'quote-management/quote-template-data',
      () => {
        setQuantityChanges({});
        setIsUpdating(false);
        setUpdateNotificationState({
          type: 'success',
          message: updateSuccessMessage,
        });
        // Auto-close success notification after delay
        setTimeout(() => {
          setIsUpdateModalOpen(false);
          setUpdateNotificationState({ type: null, message: '' });
          onUpdateModalStateChange?.(false);
        }, NOTIFICATION_AUTO_DISMISS_DELAY);
      }
    );
    return () => templateDataEvent?.off();
  }, [updateSuccessMessage, onUpdateModalStateChange]);

  const handleQuantityChange = useCallback(
    (item: CartItemModel, newQuantity: number) => {
      const cartItem = item;
      if (!cartItem.uid) {
        return;
      }
      const itemUid = cartItem.uid;
      setQuantityChanges((prev) => ({
        ...prev,
        [itemUid]: newQuantity,
      }));
    },
    []
  );

  const handleUpdate = useCallback((e: SubmitEvent) => {
    e.preventDefault();
    setUpdateNotificationState({ type: null, message: '' });
    setIsUpdateModalOpen(true);
  }, []);

  const handleConfirmUpdate = useCallback(async () => {
    if (!templateId || Object.keys(quantityChanges).length === 0) {
      setIsUpdateModalOpen(false);
      return;
    }

    setIsUpdating(true);
    setUpdateNotificationState({ type: null, message: '' });

    // Map quantity changes to API format
    const items = Object.entries(quantityChanges).map(([itemId, quantity]) => ({
      itemId,
      quantity,
    }));

    try {
      await updateQuoteTemplateItemQuantities({
        templateId,
        items,
      });
      // Success is handled by the event listener
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : updateErrorMessage;
      setUpdateNotificationState({
        type: 'error',
        message: errorMessage,
      });
      setIsUpdating(false);
    }
  }, [templateId, quantityChanges, updateErrorMessage]);

  const handleCancelUpdate = useCallback(() => {
    setIsUpdateModalOpen(false);
    setUpdateNotificationState({ type: null, message: '' });
    onUpdateModalStateChange?.(false);
    setQuantityChanges({});
  }, [onUpdateModalStateChange]);

  const handleDismissBanner = useCallback(() => {
    setUpdateNotificationState({ type: null, message: '' });
  }, []);

  return {
    quantityChanges,
    handleQuantityChange,
    handleUpdate,
    handleConfirmUpdate,
    handleCancelUpdate,
    handleDismissBanner,
    isUpdateModalOpen,
    isUpdating,
    updateNotificationState,
  };
};

